import React, { useEffect, useState } from "react";
import { BlackButton } from "../../components/black-button";
import { ItemCountInput } from "../../components/item-count-input";
import api from "../../api/api";
import { environment } from "../../environments/environment";
import { Link, useNavigate } from "react-router-dom";
import { FiTrash2 } from "react-icons/fi";
import { showCurrency } from "../../utility/currencyView";
import { IAppState } from "../../store/store";
import { useSelector } from "react-redux";

interface CartItem {
  amount: number;
  product_title: string;
  product_uuid: string;
  cart_item_uuid: string;
  image_url: string;
  quantity: number;
  size: string;
  title: string;
  unit_price: number;
}

interface CartTotal {
  total_amount_ghs: number;
  total_amount_ngn: number;
  total_amount_zar: number;
}

interface ApiCartItem {
  amount_ngn?: number;
  product_title?: string;
  product_uuid?: string;
  cart_item_uuid?: string;
  image_url?: string;
  quantity?: number;
  size?: string;
  title?: string;
  unit_price_ghs?: number | string;
  unit_price_ngn?: number | string;
  unit_price_zar?: number | string;
}

export const Cart = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: IAppState) => state.auth.token);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartTotal, setCartTotal] = useState<CartTotal | { total_amount_ghs: 0, total_amount_ngn: 0, total_amount_zar: 0 }>({
    total_amount_ghs: 0,
    total_amount_ngn: 0,
    total_amount_zar: 0
  });
  const currencyView = useSelector((state: IAppState) =>
    showCurrency(state.shared.currency)
  );

  // Helper function to get the correct unit price based on currency
  const getUnitPrice = (item: ApiCartItem): number => {
    if (currencyView === "GH₵" && item.unit_price_ghs !== undefined) {
      return parseFloat(String(item.unit_price_ghs)) || 0;
    } else if (currencyView === "NGN" && item.unit_price_ngn !== undefined) {
      return parseFloat(String(item.unit_price_ngn)) || 0;
    } else if (currencyView === "ZAR" && item.unit_price_zar !== undefined) {
      return parseFloat(String(item.unit_price_zar)) || 0;
    }
    return 0;
  };

  // Helper function to get the correct total based on currency
  const getCurrentTotal = (): number => {
    if (!cartTotal) return 0;
    
    if (currencyView === "GH₵") {
      return parseFloat(String(cartTotal.total_amount_ghs)) || 0;
    } else if (currencyView === "NGN") {
      return parseFloat(String(cartTotal.total_amount_ngn)) || 0;
    } else if (currencyView === "ZAR") {
      return parseFloat(String(cartTotal.total_amount_zar)) || 0;
    }
    return 0;
  };

  const fetchCartItems = async () => {
    const cart_uuid = localStorage.getItem("cart_uuid");

    if (cart_uuid) {
      try {
        const response = await api.get(
          `${environment.baseUrl}/product/checkout?cart_uuid=${cart_uuid}`
        );

        // Handle case when API response doesn't include cart items or data is malformed
        if (response.data?.data) {
          // Transform API response to match CartItem interface
          const items = (response.data.data.cart_items || []).map((item: ApiCartItem) => ({
            amount: parseFloat(String(item.amount_ngn)) || 0,
            product_title: item.product_title || "",
            product_uuid: item.product_uuid || "",
            cart_item_uuid: item.cart_item_uuid || "",
            image_url: item.image_url || "",
            quantity: parseInt(String(item.quantity)) || 0,
            size: item.size || "",
            title: item.title || "",
            unit_price: getUnitPrice(item)
          }));
          
          setCartItems(items);

          // Set total amounts
          setCartTotal({
            total_amount_ghs: parseFloat(String(response.data.data.total_amount_ghs)) || 0,
            total_amount_ngn: parseFloat(String(response.data.data.total_amount_ngn)) || 0,
            total_amount_zar: parseFloat(String(response.data.data.total_amount_zar)) || 0
          });
        } else {
          // Handle missing data in response
          console.error("Invalid response format from API");
          setCartItems([]);
          setCartTotal({
            total_amount_ghs: 0,
            total_amount_ngn: 0,
            total_amount_zar: 0
          });
        }
      } catch (error) {
        console.error("Error fetching cart items:", error);
        setCartItems([]);
        setCartTotal({
          total_amount_ghs: 0,
          total_amount_ngn: 0,
          total_amount_zar: 0
        });
      }
    } else {
      // No cart UUID found in localStorage
      setCartItems([]);
      setCartTotal({
        total_amount_ghs: 0,
        total_amount_ngn: 0,
        total_amount_zar: 0
      });
    }
  };

  useEffect(() => {
    fetchCartItems();
  }, []);

  // Additional useEffect to update unit prices when currency changes
  useEffect(() => {
    if (cartItems.length > 0) {
      // Re-fetch to get updated prices based on selected currency
      fetchCartItems();
    }
  }, [currencyView]);

  const handleRemoveItem = async (cartItemId: string) => {
    const cart_uuid = localStorage.getItem("cart_uuid");

    if (cart_uuid) {
      try {
        await api.delete(`${environment.baseUrl}/cart/item/delete?cart_uuid=${cart_uuid}&cart_item_uuid=${cartItemId}`);
        fetchCartItems();
      } catch (error) {
        console.error("Error removing item:", error);
      }
    }
  };

  const handleClearCart = async () => {
    const cart_uuid = localStorage.getItem("cart_uuid");
    if (cart_uuid && cartItems.length > 0) {
      if (window.confirm("Are you sure you want to clear your entire cart?")) {
        try {
          await api.delete(`${environment.baseUrl}/cart/delete?cart_uuid=${cart_uuid}`);
          // Reset cart state
          setCartItems([]);
          setCartTotal({
            total_amount_ghs: 0,
            total_amount_ngn: 0,
            total_amount_zar: 0
          });
          localStorage.removeItem("cart_uuid");
        } catch (error) {
          console.error("Error clearing cart:", error);
        }
      }
    }
  };

  // Safe calculation for item price
  const calculateItemTotal = (item: CartItem): string => {
    if (!item || typeof item.unit_price !== 'number' || typeof item.quantity !== 'number') {
      return "0.00";
    }
    return (item.unit_price * item.quantity).toFixed(2);
  };

  return (
    <>
      <main className="web_padding_x web_padding_y max-w-[2000px]">
        <h2 className="web_heading bar_above">Your Cart</h2>

        <div className="grid mt-8 gap-7 xl:grid-cols-[65%_35%] items-start">
          <div className="rounded-[20px] border border-solid border-[#EBEBEB] p-8">
            <div className="flex justify-between items-center">
              <div className="font-bold text-lg">( {cartItems.length} items )</div>
              {cartItems.length > 0 && (
                <button
                  onClick={handleClearCart}
                  className="px-4 py-2 rounded-md text-red-500 border border-red-500 hover:bg-red-50 transition-colors"
                >
                  Clear Cart
                </button>
              )}
            </div>

            <div className="mt-10 flex flex-col gap-10">
              {cartItems.length > 0 ? (
                cartItems.map((item, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row gap-2 md:gap-5 items-center border-b pb-6 last:border-b-0">
                    {/* Trash icon positioned at top right corner */}
                    <button
                      className="absolute top-0 right-0 p-2 rounded-full hover:bg-gray-100 transition-colors"
                      onClick={() => handleRemoveItem(item.cart_item_uuid)}
                      aria-label="Remove item"
                    >
                      <span className="text-red-500">
                        {React.createElement(FiTrash2, { size: 20 })}
                      </span>
                    </button>

                    <div className="w-[132px] h-[196px] rounded-[27px] bg-[#EBEBEB] overflow-hidden">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex flex-col md:self-start flex-grow">
                      <h4 className="font-bold text-xl pr-8">
                        <Link to={`/products/${item.product_uuid}`}>{item.product_title}</Link>
                      </h4>
                      {item.size && <p className="text-gray-500">Size: {item.size}</p>}
                      <p className="text-gray-500">Product: {item.product_title}</p>
                      <p className="font-medium mt-1">Quantity: {item.quantity}</p>
                    </div>
                    <div className="md:self-end mt-2 md:mt-0">
                      <div className="text-center md:text-right font-bold text-[30px] sm:text-[37px]">
                        {currencyView} {calculateItemTotal(item)}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center py-8">Your cart is empty.</p>
              )}
            </div>
          </div>

          <div className="rounded-[20px] border border-solid border-[#EBEBEB] pt-8 pb-6">
            <BlackButton
              onClick={() => {
                navigate("/product-checkout")
                sessionStorage.setItem("cart_uuid", localStorage.getItem("cart_uuid") || "");
              }}
              text="Proceed to checkout"
              className={`block mx-auto w-[80%] md:w-[70%] ${cartItems.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              isDisabled={cartItems.length === 0}
            ></BlackButton>

            <div className="flex justify-between pt-4 pb-2 px-6 md:px-12 font-bold">
              <div>Subtotal ({cartItems.length} items)</div>
              <div>{currencyView} {getCurrentTotal().toFixed(2)}</div>
            </div>
            <div className="mx-3 h-[1px] bg-[#EBEBEB] mt-2"></div>
            <div className="flex justify-between pt-4 pb-2 px-6 md:px-12 font-bold">
              <div>Estimated Total</div>
              <div>{currencyView} {getCurrentTotal().toFixed(2)}</div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};